import { useCallback, useMemo } from 'react';
import { NotificationQueries } from '@frontend/api-notifications';
import { allNotificationTypes } from '@frontend/settings-routing';
import { GetWeavePopNotificationByType, PopupDuration, WeavePopNotification } from '@frontend/types';
import { createDesktopNotification } from '../create-desktop-notification';
import { useNotificationContext } from '../notification-provider';
import { useNotificationQueue } from '../notification-queue';
import { useNotificationSettings } from '../use-notification-settings';

export type CreateNotification<T extends WeavePopNotification['type']> = (
  notification: GetWeavePopNotificationByType<T>,
  options?: {
    os?: Parameters<typeof createDesktopNotification>[0] | undefined;
    track?: boolean;
    force?: boolean;
    soundId?: string;
  }
) => void;

export const useNotification = <T extends WeavePopNotification['type']>() => {
  const { data } = NotificationQueries.useNotificationSettingsQuery();
  const { maxLocationsSelected } = useNotificationContext();
  const { add, remove, update } = useNotificationQueue();
  const { hasChangedDuration, notificationSettings } = useNotificationSettings();
  // map user's notification settings to the allowed notification types
  const mappedNotificationSettings = useMemo(() => {
    return allNotificationTypes.map((allowedType) => ({
      type: allowedType.type,
      canNotify: data?.find((notificationType) => {
        return notificationType.id === allowedType.id || allowedType.type === 'device-connection';
      })?.sendNotification,
    }));
  }, [data]);

  const create: CreateNotification<T> = useCallback(
    (notification, _options) => {
      if (maxLocationsSelected) {
        return;
      }
      const notificationType = mappedNotificationSettings.find((item) => item.type === notification.type);

      const notificationDuration = notificationSettings.durationMs;

      if (!notificationType?.canNotify && !_options?.force) {
        return;
      }
      add(
        {
          ...notification,
          state: {
            timeout: !hasChangedDuration ? PopupDuration.Manual : notificationDuration,
            ...notification.state,
          },
        },
        undefined,
        _options?.soundId
      );
      // I think this is commented out because we handle create the desktop notifications manually in the websocket listeners now
      // if (options?.os) {
      //   createDesktopNotification(options.os);
      // }
    },
    [add, maxLocationsSelected, mappedNotificationSettings]
  );

  const updateNotification = useCallback(
    (notification: GetWeavePopNotificationByType<T>) => {
      update(notification);
    },
    [update]
  );

  const removeNotification = useCallback(
    (id: string) => {
      remove(id);
    },
    [remove]
  );

  return {
    create,
    remove: removeNotification,
    update: updateNotification,
  };
};
